import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router';

// Types
import type { WCmsPageView } from '@zola/svc-web-api-ts-client';

// Zola-ui
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';

// React-Final-Form
import { Form, FormSpy } from 'react-final-form';
import { EditPageV2Props } from '.';
import * as Validations from '../../../../../../common/form/commonValidations';

// Components
import FileInputZolaUIButton from '../../../../../../common/form/FileInputZolaUIButton';
import FileInput from '../../../../../../common/form/FileInput';
import {
  FormHiddenInput,
  FormInput,
  FormTextarea,
} from '../../../../../../common/form/FinalFormV2Elements';

// Content
import { getCmsPageDescriptionPlaceholder } from '../../../../CmsPageTypes';

// Styles
import '../EditPageForm.less';

const EditPageFormV2: React.FC<EditPageV2Props> = ({
  page,
  headerImgUrl,
  filePreview,
  onUpload,
  router,
  routes,
  submitPageSilently,
  submitPage,
  updatePreview,
}) => {
  const pageType = page && page.type;
  const descriptionPlaceholder = getCmsPageDescriptionPlaceholder(pageType);
  const filePreviewUrl = filePreview && filePreview.cloudfront_url;
  const headerImagePreview = filePreviewUrl || headerImgUrl;
  const hasHeaderImage = !!headerImgUrl;
  // if true, the form hasn't been submitted with the newly uploaded image
  const [needsUpdate, setNeedsUpdate] = useState(false);

  // upload the file and tell the form it will need to submit itself
  const onUploadWithUpdate = (file: File, onFailCB: () => void) => {
    onUpload(file, onFailCB);
    setNeedsUpdate(true);
  };

  // submit the page whenever the headerImage is updated
  // AND the imageFlags are different
  // ensures header image is saved
  useEffect(() => {
    const submit = async () => {
      if (needsUpdate) {
        await submitPageSilently(page)
          .then(() => setNeedsUpdate(false))
          .catch();
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHeaderImage]);

  useEffect(() => {
    return () => {
      router.setRouteLeaveHook(routes[routes.length - 1], () => {
        // do nothing
      });
    };
  });

  return (
    <Form
      onSubmit={async (values: WCmsPageView) => {
        await submitPage(values);
      }}
      initialValues={page}
    >
      {({ handleSubmit, form: { change, getState } }) => (
        <form onSubmit={handleSubmit} id="edit-page-form" className="edit-page-form" noValidate>
          <FormHiddenInput id="cms-page-id" name="id" />
          <FormHiddenInput id="cms-page-hidden" name="hidden" />
          <FormHiddenInput name="header_image_id" />
          <FormHiddenInput name="header_image_url" />
          <div className="title-photo-section form-group">
            <FormInput
              name="title"
              type="text"
              label="Page Title"
              id="cms-page-title"
              required
              maxChars={40}
              validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
              onBlurFunction={async () => {
                const { dirty, valid } = getState();
                if (dirty && valid) {
                  await handleSubmit();
                }
              }}
              className="page-title-field"
            />
            {!hasHeaderImage && (
              <FileInputZolaUIButton
                types="image/*"
                inputId={`header_image_${page.type}`}
                onSelect={onUploadWithUpdate}
                btnText="Add A Photo"
                divClass="upload-image-button"
                btnClass="v2-button secondary-button"
              />
            )}
          </div>
          {hasHeaderImage && (
            <div className="page-image header-image form-group">
              <div className="form-label">
                Main Image <small>(max file size 5 MB)</small>
              </div>
              <div className="edit-image-section">
                <div className="image-preview">
                  <FileInput
                    filePreview={headerImagePreview}
                    inputId={`header_image_${page.type}`}
                    onSelect={onUploadWithUpdate}
                  />
                </div>
                <ButtonV2
                  className="image-delete-button"
                  variant="tertiary"
                  onClick={async () => {
                    change('header_image_id', undefined);
                    change('header_image_url', undefined);
                    await submitPageSilently(getState().values);
                  }}
                >
                  Delete
                </ButtonV2>
              </div>
            </div>
          )}
          <FormTextarea
            name="description"
            id="cms-page-description"
            label="Description"
            placeholder={descriptionPlaceholder}
            maxChars={1000}
            rows={4}
            validate={[Validations.MAX_LENGTH_1000]}
            onBlur={async () => {
              const { dirty, valid } = getState();
              if (dirty && valid) {
                await handleSubmit();
              }
            }}
            className="page-description"
          />
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values }) => {
              let { title, description, hidden, header_image_url } = values;
              title = title || '';
              description = description || '';
              hidden = hidden || false;
              header_image_url = header_image_url || '';
              updatePreview({ title, description, hidden, header_image_url });
            }}
          />
        </form>
      )}
    </Form>
  );
};

export const EditPageFormV2WithRouter = withRouter(EditPageFormV2);
